// File: AdminUserAdvancePaymentsPage.js
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import SearchIcon from "@mui/icons-material/Search";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { getUserAdvancesAdminApi } from "../../api/overtimeAndAdvanceApi";

const COLUMN_COUNT = 4;

const formatNaturalDate = (day, month, year) => {
  if (!day || !month || !year) return "-";
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

function AdminUserAdvancePaymentsPage() {
  const location = useLocation();
  const { user_id, user_full_name } = location.state || {};

  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();

  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i).reverse();

  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);
  const [advances, setAdvances] = useState([]);
  const [filteredAdvances, setFilteredAdvances] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchAdvances = async () => {
    if (!user_id) return;

    setLoading(true);

    try {
      const res = await getUserAdvancesAdminApi({
        user_id,
        start_date: `${year}-${String(month).padStart(2, "0")}-01`,
        end_date: `${year}-${String(month).padStart(2, "0")}-31`,
      });

      if (res.ok && res.data?.status === "success") {
        const records = Array.isArray(res.data?.message) ? res.data.message : [];
        setAdvances(records);
        setFilteredAdvances(records);
      } else {
        setAdvances([]);
        setFilteredAdvances([]);
      }
    } catch (err) {
      console.error(err);
      setAdvances([]);
      setFilteredAdvances([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdvances();
  }, [month, year, user_id]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredAdvances(advances);
      return;
    }

    const term = searchTerm.toLowerCase();

    const result = advances.filter(
      (a) =>
        (a.remarks || "").toLowerCase().includes(term) ||
        formatNaturalDate(a.day, a.month, a.year).toLowerCase().includes(term) ||
        (a.approved_by || "").toLowerCase().includes(term)
    );

    setFilteredAdvances(result);
  }, [searchTerm, advances]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <MDBox p={3} mb={3} bgColor="white" borderRadius="lg">
          <MDTypography variant="h5" fontWeight="bold" mb={2}>
            Advances for {user_full_name || "User"}
          </MDTypography>

          {/* Filters */}
          <Grid container spacing={2} mb={2}>
            <Grid item xs={6} md={2}>
              <TextField
                select
                label="Month"
                fullWidth
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                sx={{ "& .MuiInputBase-root": { minHeight: 48 } }}
              >
                {months.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6} md={2}>
              <TextField
                select
                label="Year"
                fullWidth
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                sx={{ "& .MuiInputBase-root": { minHeight: 48 } }}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Search by remarks, date, or approver"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: { backgroundColor: "white", borderRadius: 2, minHeight: 48 },
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <MDButton
                variant="gradient"
                color="info"
                onClick={fetchAdvances}
                disabled={loading}
                sx={{ minHeight: 48 }}
              >
                {loading ? <CircularProgress size={20} color="white" /> : "Refresh"}
              </MDButton>
            </Grid>
          </Grid>

          {/* Table */}
          <TableContainer component={Paper} sx={{ maxHeight: 600, boxShadow: "none" }}>
            <Table stickyHeader aria-label="user advances table">
              <TableBody>
                <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Amount (KES)</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Remarks</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Approved By</TableCell>
                </TableRow>

                {loading ? (
                  <TableRow>
                    <TableCell colSpan={COLUMN_COUNT} align="center">
                      <CircularProgress size={24} />
                      <MDTypography mt={1} variant="body2">
                        Loading advances...
                      </MDTypography>
                    </TableCell>
                  </TableRow>
                ) : filteredAdvances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={COLUMN_COUNT} align="center">
                      <MDTypography variant="body2">
                        No advances found for {month}/{year}.
                      </MDTypography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAdvances.map((advance) => (
                    <TableRow
                      key={advance.advance_id}
                      sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}
                    >
                      <TableCell>
                        {formatNaturalDate(advance.day, advance.month, advance.year)}
                      </TableCell>

                      <TableCell>
                        <MDTypography variant="body2" fontWeight="bold" color="info">
                          KES {advance.amount ?? 0}
                        </MDTypography>
                      </TableCell>

                      <TableCell>{advance.remarks || "-"}</TableCell>
                      <TableCell>{advance.approved_by || "-"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </MDBox>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default AdminUserAdvancePaymentsPage;
